import { useCart } from '@/components/Cart';
import { useInvoices } from '@/components/Invoice';
import PaymentForm from '@/components/PaymentForm';
import { api } from '@/lib/api';
import { downloadFile } from '@/lib/downloadFile';
import { CheckoutRequest, InvoiceResponse, LineItem } from '@/types';
import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconCreditCard,
  IconDownload,
  IconReceipt,
  IconShoppingCart,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const Checkout: React.FC = () => {

  useEffect(() => {
    document.title = "Checkout - Guard Dog Real Estate Protection Service";
  }, []);

  const { items, total, loading: cartLoading, clearLocalCart } = useCart();
  const { addLineItems } = useInvoices();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState<InvoiceResponse | null>(null);
  const [attemptId, setAttemptId] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const handleCheckout = useCallback(
    async (request: CheckoutRequest) => {
      if (items.length === 0) {
        notifications.show({
          title: 'Cart is Empty',
          message: 'Please add items to your cart before checkout',
          color: 'orange',
          position: 'top-center',
          "aria-live": "polite",
        });
        return;
      }

      setSubmitting(true);

      try {
        const response: InvoiceResponse = await api.post('/checkout', request);

        addLineItems(response.items);
        clearLocalCart();
        setCheckoutResult(response);
      } catch (error: unknown) {
        console.error('Checkout failed:', error);
        setAttemptId((prev) => prev + 1);
      } finally {
        setSubmitting(false);
      }
    },
    [items.length, addLineItems, clearLocalCart]
  );

  const handleBackToCart = () => {
    navigate('/cart');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleDownload = async (doc: LineItem) => {
    setDownloadingId(doc.id);
    try {
      await downloadFile(`/document/download-purchased/${doc.id}`, `${doc.documentNumber}.pdf`);
    } finally {
      setDownloadingId(null);
    }
  };

  const isLoading = cartLoading || submitting;

  // Success state - payment completed
  if (checkoutResult) {
    return (
      <Box role="status" aria-live="polite">
        <Stack gap="md">
          <Group justify="center" align="center">
            <Group gap="xs">
              <IconCheck size={28} color="var(--mantine-color-green-6)" aria-hidden />
              <Title order={2}>Payment Successful</Title>
            </Group>
          </Group>

          <Paper p="sm" >
            <Stack align="center" gap="lg">
              <IconReceipt size={64} color="var(--mantine-color-green-6)" aria-hidden />

              <Stack align="center" gap="xs">
                <Text size="xl" fw={700}>
                  Thank you for your purchase!
                </Text>
              </Stack>

              <Paper p="md" withBorder w="100%" maw={400}>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text c="dimmed">Invoice:</Text>
                    <Text fw={500}>INV-{checkoutResult.invoiceId}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text c="dimmed">Amount:</Text>
                    <Text fw={500}>{formatPrice(checkoutResult.totalAmount)}</Text>
                  </Group>
                  {checkoutResult.cardLast4 && (
                    <Group justify="space-between">
                      <Text c="dimmed">Card:</Text>
                      <Text fw={500}>
                        ****{checkoutResult.cardLast4} ({checkoutResult.cardType})
                      </Text>
                    </Group>
                  )}
                  <Group justify="space-between">
                    <Text c="dimmed">Items:</Text>
                    <Text fw={500}>{checkoutResult.items.length} document{checkoutResult.items.length > 1 ? 's' : ''}</Text>
                  </Group>
                </Stack>
              </Paper>

              {checkoutResult.items.length > 0 && (
                <Paper p="md" withBorder w="100%" maw={400}>
                  <Stack gap="sm">
                    <Text fw={600}>Download your document{checkoutResult.items.length > 1 ? 's' : ''}</Text>
                    {checkoutResult.items.map((item) => (
                      <Group key={item.id} justify="space-between" wrap="nowrap">
                        <Stack gap={0} style={{ minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate>
                            {item.documentNumber}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {item.county}
                          </Text>
                        </Stack>
                        <Button
                          variant="light"
                          color="pq-red"
                          size="md"
                          leftSection={<IconDownload size={16} aria-hidden />}
                          loading={downloadingId === item.id}
                          onClick={() => handleDownload(item)}
                          aria-label={`Download ${item.documentNumber}`}
                        >
                          Download
                        </Button>
                      </Group>
                    ))}
                    <Text size="xs" c="dimmed">
                      Your document{checkoutResult.items.length > 1 ? 's' : ''} will be sent to your email shortly, 
                      and you can download {checkoutResult.items.length > 1 ? 'them' : 'it'} from your Dashboard for 12 months.
                    </Text>
                  </Stack>
                </Paper>
              )}

                <Button variant="outline" color="gray" onClick={handleGoToDashboard}>
                  Back to Dashboard
                </Button>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    );
  }

  return (
    <Box pos="relative">
      <LoadingOverlay
        aria-live="polite"
        aria-label="Loading"
        visible={isLoading}
        overlayProps={{ blur: 2, radius: 'sm' }}
        loaderProps={{ size: 'xl' }}
      />

      <Stack gap="md" maw={850} mx="auto">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconCreditCard size={28} color="var(--mantine-color-pq-red-6)" aria-hidden />
            <Title order={2}>Checkout</Title>
          </Group>
          <Button variant="outline" color="gray" onClick={handleBackToCart}>
            Back to Cart
          </Button>
        </Group>

        {items.length === 0 && !cartLoading && !submitting ? (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <IconShoppingCart size={64} color="var(--mantine-color-gray-5)" aria-hidden />
              <Text c="dimmed" size="lg">
                Your cart is empty
              </Text>
              <Text c="dimmed" size="sm">
                Add documents from your dashboard alerts to get started.
              </Text>
              <Button color="pq-red" onClick={handleGoToDashboard}>
                Go to Dashboard
              </Button>
            </Stack>
          </Paper>
        ) : (
          <>
            {/* Order Summary */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Order Summary</Title>
                <Box style={{ overflowX: 'auto' }}>
                  <Table style={{ minWidth: 650 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Document</Table.Th>
                        <Table.Th>APN</Table.Th>
                        <Table.Th>Property Address</Table.Th>
                        <Table.Th>County</Table.Th>
                        <Table.Th>Date</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Price</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {items.map((item) => (
                        <Table.Tr key={item.id}>
                          <Table.Td>
                            <Text fw={500}>{item.documentNumber}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{item.apn || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" style={{ maxWidth: 180 }} lineClamp={2}>
                              {item.propertyAddress || 'N/A'}
                            </Text>
                          </Table.Td>
                          <Table.Td>{item.county}</Table.Td>
                          <Table.Td>
                            {item.documentDate
                              ? dayjs(item.documentDate).format('MM/DD/YYYY')
                              : '-'}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {formatPrice(item.price)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Box>

                {/* Total */}
                <Group justify="flex-end" gap="xs">
                  <Text size="lg" fw={600}>
                    Total:
                  </Text>
                  <Text size="lg" fw={700} c="pq-red">
                    {formatPrice(total)}
                  </Text>
                </Group>
              </Stack>
            </Paper>

            {/* Payment Form */}
            <PaymentForm
              amount={total}
              onSubmit={handleCheckout}
              disabled={submitting || items.length === 0}
              resetTrigger={attemptId}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default Checkout;
